/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { FC, ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router'
import { useServiceContext } from '../../root'
import { TaskViewType, Task, TaskStatus, Role } from './types'
import { UserRole } from '../../types/access'
import TaskTypeLink from './TaskTypeLink'
import TaskDocLink from '../../components/shared/TaskDocLink'
import TaskMenu from './TaskMenu'
import { RcmTable } from '../../components/rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../components/table/TableComponents'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import WorkflowDetailsModal from '../../components/modals/WorkflowDetailsModal'
import TaskDetailsModal from './Actions/details'
import TextLinkButton from '../../components/shared/TextLinkButton'
import { useModal } from '../../contexts/ModalContext'
import { toast } from '../../services/toast'
import { formatUserDateTime } from '~/utils/dateFormatting'

interface TasksTableProps {
  viewType: TaskViewType
  filterShown: boolean
  currentUserId?: number
  isEosEnabled?: boolean
  hidePaginate?: boolean
  TableFooter?: ReactNode
}

/**
 * Tasks Table Component
 *
 * Displays tasks in a paginated, sortable table with conditional columns
 * based on EOS and ETMF feature flags.
 *
 * Migrated from lego/page/tasks/Table.jsx with full feature parity:
 * - EOS view derived from props.isEosEnabled OR filter state (isEosEnabled + role/isEosSelected)
 * - ETMF view derived from feature flags, reactive to tableData.entries changes
 * - Deep-link task detail opening via URL param with permission check
 * - EOS-specific redirect logic for non-OPEN tasks (USER role → dashboard + toast)
 * - disableOnInitLoad support when filter is shown
 * - Uses screen-level TaskDocLink (icons, states, multi-doc support)
 * - Uses TextLinkButton for workflow CR link (matching legacy TextLinkButton)
 * - Uses TableDateTimeCell for date rendering with NOT_AVAILABLE fallback
 */
const TasksTable: FC<TasksTableProps> = ({
  viewType,
  filterShown,
  currentUserId,
  isEosEnabled: isEosEnabledProp,
  hidePaginate,
  TableFooter
}) => {
  const { t } = useTranslation()
  const { service, api } = useServiceContext()
  const tableData = useRcmTableData()
  const { showModal } = useModal()
  const navigate = useNavigate()

  // Read task ID from URL params for deep-linking (matches legacy $state.params.id)
  const { id: taskIdParam } = useParams<{ id?: string }>()

  const auth = service?.auth
  const featuresInfo = service?.init?.getFeaturesInfo?.()

  // EOS and ETMF view state — reactive to filter/entries changes (matches legacy useEffect on tableData.entries)
  const [isEosView, setIsEosView] = useState(false)
  const [isEtmfView, setIsEtmfView] = useState(false)

  /**
   * Derive isEosView from props and filter state.
   * Matches legacy: props.isEosEnabled ? true : tableData.filter?.isEosEnabled && (auth.getRole() === Role.USER || tableData.filter?.isEosSelected)
   *
   * Logic: Show EOS columns if:
   *   1. Parent explicitly enables EOS (isEosEnabledProp), OR
   *   2. EOS feature is enabled (from filter context or feature flags)
   *      AND (user is a regular USER OR the EOS checkbox is selected in filters)
   */
  useEffect(() => {
    const filterEosEnabled = tableData.filter?.isEosEnabled ?? (featuresInfo?.eosFeature?.show || false)
    const filterEosSelected = tableData.filter?.isEosSelected || false
    const userRole = auth?.getRole?.()

    setIsEosView(isEosEnabledProp ? true : filterEosEnabled && (userRole === Role.USER || filterEosSelected))
    setIsEtmfView(featuresInfo?.etmfFeature?.show || false)
  }, [tableData.entries, tableData.filter, isEosEnabledProp, auth, featuresInfo])

  /**
   * Deep-link: Open TaskDetailsModal when a task ID is present in URL params.
   * Matches legacy useEffect on $state.params.id:
   *   1. Check permission (contentAdmins, USER, AUDITOR, POWER_USER)
   *   2. Show TaskDetailsModal if allowed
   *   3. If EOS enabled + USER role: validate task status, redirect to dashboard + toast for non-OPEN tasks
   */
  useEffect(() => {
    const taskId = Number(taskIdParam)
    if (!taskId) return

    const currentUserRole = auth?.getRole?.()
    const isEosUser = featuresInfo?.eosFeature?.show && currentUserRole === Role.EOS_SITE_USER

    if (isEosUser) {
      api.tasks
        .getTask(String(taskId))
        .then((data: Task) => {
          navigate('/overview', { replace: true })
          if (data?.status !== TaskStatus.OPEN) {
            const messageText =
              data?.status === TaskStatus.APPROVED
                ? 'EOS_TASK_COMPLETED'
                : data?.status === TaskStatus.CANCELLED
                  ? 'EOS_TASK_CANCELED'
                  : 'EOS_TASK_DELETED_REASSIGNED'
            toast.info(messageText)
          }
        })
        .catch((error: { data?: { code?: number; message?: string } }) => {
          navigate('/overview', { replace: true })
          toast.error(
            error?.data?.code === 404 || error?.data?.code === 403
              ? 'EOS_TASK_DELETED_REASSIGNED'
              : error?.data?.message || 'TASK_DETAIL_NOT_EXIST_MSG'
          )
        })
      return
    }

    // Permission check: matches legacy access.isAllowedStateAsync([accessTable.contentAdmins, 'USER', 'AUDITOR', 'POWER_USER'])
    const isAllowed = service?.access?.isAllowed?.([
      UserRole.ADMIN,
      UserRole.CONTENT_ADMIN,
      UserRole.ETMF_MANAGER,
      UserRole.ETMF_SPECIALIST,
      UserRole.USER,
      UserRole.AUDITOR,
      UserRole.POWER_USER
    ])
    if (isAllowed) {
      showModal(
        <TaskDetailsModal
          taskId={taskId}
          onClose={() => {
            // No-op: modal hides itself via ModalContext
          }}
        />
      )
    }
  }, [taskIdParam])

  const excerpt = (text: string | undefined, html: boolean, length: number): string => {
    if (!text) return ''
    if (text.length <= length) return text
    return text.substring(0, length) + '...'
  }

  return (
    <RcmTable
      className="tasks-list-table chita-table"
      noResultsText="NO_RESULTS"
      disableOnInitLoad={filterShown}
      hidePaginate={hidePaginate}
      TableFooter={TableFooter}
      HeadRow={
        <TableHeadRow>
          <TableHeadCell sort="TASK_TYPE">{t('TASKS_TABLE_HEADER_NAME')}</TableHeadCell>
          {isEosView && (
            <TableHeadCell id="6b01875a-16ad-46a1-93d6-f51e40e5aa1d" style={{ fontSize: '12px' }}>
              {t('EMTF_MD_STUDY')}
            </TableHeadCell>
          )}
          {isEosView && (
            <TableHeadCell id="f87b9b07-c065-40c3-8f85-5a678dde9d3b" style={{ fontSize: '12px' }}>
              {t('EMTF_MD_SITE')}
            </TableHeadCell>
          )}
          {!isEosView && !isEtmfView && (
            <TableHeadCell id="desc" style={{ fontSize: '12px' }}>
              {t('TASKS_TABLE_HEADER_DESC')}
            </TableHeadCell>
          )}
          {!isEosView && (
            <TableHeadCell id="docname" style={{ fontSize: '12px' }}>
              {t('DOCUMENT_NAME')}
            </TableHeadCell>
          )}
          <TableHeadCell sort="WORKFLOW">{t('CHANGE_REQUEST')}</TableHeadCell>
          <TableHeadCell sort="ASSIGNED_TO">{t('TASKS_TABLE_HEADER_ASSIGNED_TO')}</TableHeadCell>
          <TableHeadCell sort="START_DATE">{t('TASKS_TABLE_HEADER_START_DATE')}</TableHeadCell>
          {isEtmfView && (
            <TableHeadCell id="33dbfc0c-77f0-4cb0-ba4f-e183e0c54f5e" style={{ fontSize: '12px' }}>
              {t('EMTF_MD_STUDY')}
            </TableHeadCell>
          )}
          {isEtmfView && (
            <TableHeadCell id="dda9cdd5-639b-43d0-b086-06782e9d6482" style={{ fontSize: '12px' }}>
              {t('EMTF_MD_COUNTRY')}
            </TableHeadCell>
          )}
          {isEtmfView && (
            <TableHeadCell id="6d85991b-7453-4e9f-983b-2a003c16a600" style={{ fontSize: '12px' }}>
              {t('REPORTS_FILTER_LABEL_SITE')}
            </TableHeadCell>
          )}
          <TableHeadCell id="398d3535-135f-475a-96b6-c025635ad33c" className="actions" style={{ fontSize: '12px' }}>
            {t('TASKS_TABLE_HEADER_ACTIONS')}
          </TableHeadCell>
        </TableHeadRow>
      }
      Row={({ row, index }: { row: Record<string, unknown>; index: number }) => {
        const task = row as unknown as Task
        return (
          <TableRow key={task.id}>
            <td>
              <TaskTypeLink task={task} />
            </td>
            {!isEosView ? (
              <>
                {!isEtmfView && <td>{excerpt(task.workflowDescription, false, 80)}</td>}
                <td className="doc-name clipped-cell">
                  <TaskDocLink task={task} />
                </td>
              </>
            ) : (
              <>
                <td title={task.studyName || ''}>{task.studyName || t('NOT_AVAILABLE')}</td>
                <td title={task.siteName || ''}>{task.siteName || t('NOT_AVAILABLE')}</td>
              </>
            )}
            <td>
              <TextLinkButton
                action={() => {
                  showModal(<WorkflowDetailsModal wfId={Number(task.workflow?.id)} />)
                }}
              >
                {task.workflow?.formattedId}
              </TextLinkButton>
            </td>
            <td>{task.assignee?.name}</td>
            <td>{formatUserDateTime(Number(task.startDate))}</td>
            {isEtmfView && (
              <>
                <td>{task.filteredFileToEtmfDocDetails?.study || '-'}</td>
                <td>{task.filteredFileToEtmfDocDetails?.country || '-'}</td>
                <td>{task.filteredFileToEtmfDocDetails?.site || '-'}</td>
              </>
            )}
            <td className="actions">{task.status === TaskStatus.OPEN && <TaskMenu task={task} />}</td>
          </TableRow>
        )
      }}
    />
  )
}

export default TasksTable
